<template>
  <div class="popUpWrap">
    <section class="popup light">
      <div class="topArea">
        <h4>{{ t("profile-personal-info-title") }}</h4>
        <div class="exit" @click.stop="onClickClose"></div>
      </div>
      <div class="scrollY">
        <ul class="infoList flex">
          <li class="flex">
            <div class="tit">{{ t("profile-gender") }}</div>
            <div class="selectArea">
              <div class="selectBox">
                <button
                  id="genderButton"
                  type="button"
                  class="btnToggle"
                  :class="{selected: isSelect}"
                  @click.stop="genderMenuOpenFlag = !genderMenuOpenFlag">
                  {{ isSelectGenderString }}
                </button>
                <ul class="dropMenu scrollY" :class="{show: genderMenuOpenFlag}">
                  <li class="dropItem">
                    <button
                      id="defaultGender"
                      type="button"
                      class="dropOption"
                      @click.stop="selectGenderType(ProfilePersonalGenderType.DEFAULT)">
                      {{ t("profile-gender-drop-item-default") }}
                    </button>
                  </li>
                  <li class="dropItem">
                    <button
                      id="maleGender"
                      type="button"
                      class="dropOption"
                      @click.stop="selectGenderType(ProfilePersonalGenderType.MALE)">
                      {{ t("profile-gender-drop-item-male") }}
                    </button>
                  </li>
                  <li class="dropItem">
                    <button
                      id="femaleGender"
                      type="button"
                      class="dropOption"
                      @click.stop="selectGenderType(ProfilePersonalGenderType.FEMALE)">
                      {{ t("profile-gender-drop-item-female") }}
                    </button>
                  </li>
                  <li class="dropItem">
                    <button
                      id="otherGender"
                      type="button"
                      class="dropOption"
                      @click.stop="selectGenderType(ProfilePersonalGenderType.OTHER)">
                      {{ t("profile-gender-drop-item-other") }}
                    </button>
                  </li>
                  <li class="dropItem">
                    <button
                      id="notSayGender"
                      type="button"
                      class="dropOption"
                      @click.stop="selectGenderType(ProfilePersonalGenderType.PREFER_NOT_TO_SAY)">
                      {{ t("profile-gender-drop-item-not-say") }}
                    </button>
                  </li>
                </ul>
              </div>
            </div>
          </li>
          <li class="flex">
            <div class="tit">{{ t("profile-phone-number") }}</div>
            <input
              id="phoneNumberInput"
              v-model="modifyPersonalInfo.phoneNumber"
              type="text"
              :class="{error: !isValidStringPhoneNumber}"
              :placeholder="t('profile-phone-number-placeholder')">
            <p v-if="!isValidStringPhoneNumber" class="errorMessage"><span></span>{{ t("profile-phone-number-error") }}</p>
          </li>
          <li class="flex">
            <div class="tit">{{ t("profile-links") }}</div>
            <ul class="linkList flex">
              <li>
                <div class="flex">
                  <div class="icon linkedin"></div>
                  <input
                    id="linkedInInput"
                    v-model="modifyPersonalInfo.links[0]"
                    type="text"
                    :class="{error: !isValidStringLinkInURL}"
                    placeholder="https://www.linkedin.com/in/">
                </div>
                <p v-if="!isValidStringLinkInURL" class="errorMessage"><span></span>
                  {{ t("profile-links-error-linkedIn") }}
                </p>
              </li>
              <li>
                <div class="flex">
                  <div class="icon github"></div>
                  <input
                    id="githubInput"
                    v-model="modifyPersonalInfo.links[1]"
                    type="text"
                    :class="{error: !isValidStringGithubURL}"
                    placeholder="https://github.com/">
                </div>
                <p v-if="!isValidStringGithubURL" class="errorMessage"><span></span>{{ t("profile-links-error-gitHub") }}
                </p>
              </li>
              <li v-for="(link, index) in modifyPersonalInfo.links?.slice(2)" :key="index" class="remove">
                <div class="flex">
                  <div class="icon"></div>
                  <input v-model="modifyPersonalInfo.links[index+2]" type="text" placeholder="https://example.com">
                  <button :id="`personalLinkRemoveBtn${index}`" class="btn remove" @click.stop="onClickDeleteLink(index+2)">
                    <div></div>
                  </button>
                </div>
              </li>
              <li>
                <button class="btn textBtn add" @click.stop="onClickAddLink"><span></span> {{ t("profile-links-add") }}
                </button>
              </li>
            </ul>
          </li>
        </ul>
        <div class="btnArea">
          <button
            class="btn"
            :class="{disabled: !isValidDisableButton}"
            :disabled="!isValidDisableButton"
            @click.stop="onClickSave">
            {{ t("common-save") }}
          </button>
        </div>
      </div>
    </section>
    <div v-if="isDeletePopupOpen" class="popUpWrap">
      <section class="popup dark">
        <div class="topArea">
          <div id="linkRemovePopupExitBtn" class="exit" @click.stop="isDeletePopupOpen = false"></div>
        </div>
        <div class="scrollY">
          <h5>{{ t("profile-links-remove") }}</h5>
          <div class="btnArea">
            <button class="cancel" @click.stop="isDeletePopupOpen = false">{{ t("common-cancel") }}</button>
            <button @click.stop="onClickRemoveLink">{{ t("common-remove") }}</button>
          </div>
        </div>
      </section>
    </div>
  </div>
</template>

<script setup lang="ts">
import { useI18n } from "vue-i18n";
import { useDialogStore } from "~/store/dialog/dialogStore";
import { onMounted } from "#imports";
import { useToastStore } from "~/store/toast/toastStore";
import { useProfileService } from "~~/application/composables/service/useProfileService";
import { ProfilePersonalGenderType } from "~~/package/mapper/profile/model/ProfilePersonalGenderType";
import { ProfileUserInformationModel } from "~~/package/mapper/profile/model/ProfileUserInformationModel";
import { useProfileStore } from "~~/application/store/profile/profileStore";
import { ToastType } from "~~/application/components/Common/Toast/model/ToastType";
import { cloneDeep } from "lodash-es";

const { t } = useI18n();

const dialogStore = useDialogStore();
const toastStore = useToastStore();
const profileService = useProfileService();
const profileStore = useProfileStore();

const genderMenuOpenFlag = ref(false);
const modifyPersonalInfo = ref<ProfileUserInformationModel>(cloneDeep(dialogStore.options.props.modifyInfo));
const isSelect = ref(false);
const isDeletePopupOpen = ref(false);
const deleteLinkIndex = ref(-1);

onMounted(() => {
  isSelect.value = modifyPersonalInfo.value.gender !== ProfilePersonalGenderType.DEFAULT
});

const isSelectGenderString = computed(() => {
  switch (modifyPersonalInfo.value.gender) {
    case ProfilePersonalGenderType.DEFAULT:
      return t("profile-gender-drop-item-default");
    case ProfilePersonalGenderType.MALE:
      return t("profile-gender-drop-item-male");
    case ProfilePersonalGenderType.FEMALE:
      return t("profile-gender-drop-item-female");
    case ProfilePersonalGenderType.OTHER:
      return t("profile-gender-drop-item-other");
    case ProfilePersonalGenderType.PREFER_NOT_TO_SAY:
      return t("profile-gender-drop-item-not-say");
  }
});

const isValidStringPhoneNumber = computed(() => {
  return modifyPersonalInfo.value.phoneNumber.length <= 25;
});

const isValidStringLinkInURL = computed(() => {
  return modifyPersonalInfo.value.links[0].toLowerCase().includes("linkedin.com/in/") ||
    modifyPersonalInfo.value.links[0].length === 0;
});

const isValidStringGithubURL = computed(() => {
  return modifyPersonalInfo.value.links[1].toLowerCase().includes("github.com/") ||
    modifyPersonalInfo.value.links[1].length === 0;
});

const isValidDisableButton = computed(() => {
  return isValidStringPhoneNumber.value && isValidStringLinkInURL.value && isValidStringGithubURL.value;
});

const selectGenderType = (type: ProfilePersonalGenderType) => {
  isSelect.value = true;
  genderMenuOpenFlag.value = false;
  modifyPersonalInfo.value.gender = type;
};

const onClickAddLink = () => {
  modifyPersonalInfo.value.links.push("");
};

const onClickDeleteLink = (index: number) => {
  isDeletePopupOpen.value = true;
  deleteLinkIndex.value = index;
};

const onClickRemoveLink = () => {
  isDeletePopupOpen.value = false;
  modifyPersonalInfo.value.links.splice(deleteLinkIndex.value, 1);
  toastStore.openToast({
    toastType: ToastType.SUCCESS,
    contents: t(`profile-links-remove-success`)
  });
}

const onClickSave = () => {
  delete modifyPersonalInfo.value.aboutMeVOList;
  modifyPersonalInfo.value.links = [...modifyPersonalInfo.value.links.slice(0, 2), ...modifyPersonalInfo.value.links.slice(2).filter(link => link !== "")] ;
  profileService.modifyUserProfile(modifyPersonalInfo.value).then(response => {
    if(response.id > 0){
      dialogStore.options.confirm?.event();
      onClickClose();
    }
    else {
      dialogStore.options.close?.event();
    }
  }).catch(() => {
    dialogStore.options.close?.event();
  })
};

const onClickClose = () => {
  dialogStore.close();
};
</script>

